/**
 * Query keys do domínio PDV (catálogo, estoque, comandas, relatórios).
 *
 * Review PR#418 (IR-RQ-001): os hooks do PDV nasceram com keys inline
 * (`['club_products', ...]`, `['pdv_reports', ...]`) que NENHUMA mutação
 * invalidava por prefixo — estorno/fechamento não atualizavam a página de
 * relatórios dentro do staleTime (mesma classe de bug do header de
 * financial.ts / P3-2). Mutações do PDV invalidam `qk.pdv.all` (prefix-match
 * cobre tudo abaixo) + `qk.financial.all` quando mexem em receita.
 */
import { tuple } from "./core";

const root = ["pdv"] as const;

export const pdv = {
  /** Prefixo raiz — mutações do PDV invalidam isto (cobre tudo abaixo). */
  all: root,
  /** Prefixo do catálogo — mutações de produto invalidam só isto. */
  productsAll: tuple(root, "products"),
  /** Fichas técnicas do escopo (`club_product_components`) — flag crm.pdv.ficha_tecnica. */
  fichas: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "fichas", clubId, viewScope),
  /** Prefixo das fichas — gravar uma ficha invalida todas. */
  fichasAll: tuple(root, "fichas"),
  /** Catálogo de produtos (`club_products`). */
  products: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "products", clubId, viewScope),
  /** Prefixo das comandas abertas — mutações de item invalidam só isto. */
  openSalesAll: tuple(root, "openSales"),
  /** Formas de balcão habilitadas no catálogo do clube (RPC `pdv_formas_do_clube`). */
  formasDoClube: (clubId: string | null | undefined) => tuple(root, "formasDoClube", clubId),
  /** Comandas ABERTAS (`club_sales` status=open, com itens). */
  openSales: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "openSales", clubId, viewScope),
  /**
   * Histórico paginado de comandas fechadas/canceladas. `filtros` é a chave
   * estável do recorte (`chaveDosFiltros` no CRM) — a ordem de marcação não muda o cache.
   */
  salesHistory: (clubId: string | null | undefined, viewScope: unknown, page: number, filtros = "") =>
    tuple(root, "salesHistory", clubId, viewScope, page, filtros),
  /** Resumo do dia (vendas fechadas hoje, BRT). */
  todaySummary: (clubId: string | null | undefined, viewScope: unknown, day: string) =>
    tuple(root, "todaySummary", clubId, viewScope, day),
  /** Picker leve de alunos do PDV. */
  studentOptions: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "studentOptions", clubId, viewScope),
  /** Relatórios de vendas por período. */
  report: (
    clubId: string | null | undefined,
    viewScope: unknown,
    startDay: string,
    endDay: string,
  ) => tuple(root, "report", clubId, viewScope, startDay, endDay),
  /** Fechamento de caixa de uma janela (dia + horário BRT; vendas fechadas + estornos). */
  dailyClosing: (clubId: string | null | undefined, viewScope: unknown, startAt: string, endAtExclusive: string) =>
    tuple(root, "dailyClosing", clubId, viewScope, startAt, endAtExclusive),
  /** Venda única com itens, para o recibo não-fiscal. */
  receipt: (saleId: string | null) => tuple(root, "receipt", saleId),
  /** Situação de crédito (a prazo) de um devedor — mesma RPC que o trigger usa. */
  creditStatus: (
    clubId: string | null | undefined,
    studentId: string | null | undefined,
    cpf: string | null | undefined,
    teamUserId: string | null | undefined = null,
    teamTeacherId: string | null | undefined = null,
    teamColaboradorId: string | null | undefined = null,
  ) => tuple(root, "creditStatus", clubId, studentId, cpf, teamUserId, teamTeacherId, teamColaboradorId),
  /** Colaboradores SEM login do clube (Equipe → só PDV, 22/10/2026). */
  colaboradores: (clubId: string | null | undefined) => tuple(root, "colaboradores", clubId),
  /** Recebimentos de uma comanda a prazo. */
  settlements: (saleId: string | null | undefined) => tuple(root, "settlements", saleId),
  /** Partes de comanda dividida pagas dentro da janela do fechamento do dia (por paid_at). */
  partesDoFechamento: (
    clubId: string | null | undefined,
    viewScope: unknown,
    startAt: string,
    endAt: string,
  ) => tuple(root, "dailyClosing", clubId, viewScope, startAt, endAt, "partes"),
  /** Partes pagas com a comanda aberta (dividir comanda, 28/10/2026). */
  partes: (saleId: string | null | undefined) => tuple(root, "partes", saleId),
  /** Política de venda a prazo do clube (`club_pdv_settings`). */
  settings: (clubId: string | null | undefined) => tuple(root, "settings", clubId),
  /** Equipe do clube com o % de desconto (RPC pdv_equipe_do_clube). */
  equipe: (clubId: string | null | undefined) => tuple(root, "equipe", clubId),
  /** Consumo da equipe no PDV por período (vendas fechadas com pessoa da equipe). */
  consumoEquipe: (
    clubId: string | null | undefined,
    viewScope: unknown,
    startDay: string,
    endDay: string,
  ) => tuple(root, "consumoEquipe", clubId, viewScope, startDay, endDay),
  /**
   * Histórico de estoque de UM produto (RPC `pdv_movimentos_de_estoque`),
   * paginado. Fica sob `qk.pdv.all`: a movimentação manual (moveStock) e o
   * fechamento/estorno de comanda já invalidam isso.
   */
  stockHistory: (productId: string | null | undefined, page: number, pageSize: number) =>
    tuple(root, "stockHistory", productId, page, pageSize),
  /** Prefixo do crédito pré-pago do cliente — vender/consumir/estornar invalidam qk.pdv.all. */
  creditoAll: tuple(root, "credito"),
  /** Saldo de crédito pré-pago de um titular (RPC `pdv_credito_saldo`). */
  creditoSaldo: (
    clubId: string | null | undefined,
    studentId: string | null | undefined,
    cpf: string | null | undefined,
  ) => tuple(root, "credito", "saldo", clubId, studentId, cpf),
  /** Créditos por titular, paginado no servidor (RPC `pdv_creditos_titulares`). */
  creditosTitulares: (
    clubIds: readonly string[],
    busca: string,
    page: number,
  ) => tuple(root, "credito", "titulares", [...clubIds].sort().join(","), busca, page),
  /** Lotes de um titular (aba Créditos → detalhe). */
  creditoLotes: (clubId: string | null | undefined, titular: string | null | undefined) =>
    tuple(root, "credito", "lotes", clubId, titular),
  /** Movimentos (razão) de um titular, paginado no servidor. */
  creditoMovimentos: (clubId: string | null | undefined, titular: string | null | undefined, page: number) =>
    tuple(root, "credito", "movimentos", clubId, titular, page),
  /** Relatório de day use por período (painel + export nominal). */
  dayUse: (
    clubId: string | null | undefined,
    viewScope: unknown,
    startDay: string,
    endDay: string,
  ) => tuple(root, "dayUse", clubId, viewScope, startDay, endDay),
} as const;
